import type { TrainingExerciseDto } from '@ukinfo/shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { TRAINING_PLANS_RESOURCE } from '../lib/training';
import { PageHeader } from '../components/PageHeader';
import { ExerciseDetailDialog } from '../components/training/ExerciseDetailDialog';
import {
  EMPTY_FILTER,
  ExerciseCard,
  ExerciseFilterBar,
  ExerciseListState,
  useExerciseList,
  useFocusOptions,
  type ExerciseFilter,
} from '../components/training/ExerciseBrowser';
import { ExerciseFormDialog } from '../components/training/ExerciseFormDialog';
import { LinkButton, TestBadge } from '../components/training/common';
import { Button } from '../components/ui';

/**
 * Uebungsbibliothek: Standardkatalog und eigene Uebungen in einer Liste.
 *
 * Die Detailansicht hat eine eigene Adresse (/sport/uebungen/:id) - Einheiten
 * verlinken dorthin, und ein Link auf eine Uebung bleibt teilbar.
 */
export function ExercisesPage(): JSX.Element {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const canEdit = can(TRAINING_PLANS_RESOURCE, 'update');
  const manages = can(TRAINING_PLANS_RESOURCE, 'manage');
  // Zahl offener Hinweise am Knopf - nur fuer die, die sie bearbeiten.
  const openRemarks = useQuery({
    queryKey: ['training-remarks-open-count'],
    queryFn: () => api.get<{ count: number }>('/api/training/remarks/open-count'),
    enabled: manages,
  });

  const [filter, setFilter] = useState<ExerciseFilter>(EMPTY_FILTER);
  const [form, setForm] = useState<{ exercise: TrainingExerciseDto | null; link: boolean } | null>(null);

  const list = useExerciseList(filter);
  const focusOptions = useFocusOptions();

  return (
    <div className="space-y-4">
      <PageHeader
        title="Übungen"
        description="Standardübungen und eigene Übungen für die Trainingsplanung"
        scoped={false}
        actions={
          <>
            <span className="self-center"><TestBadge /></span>
            <LinkButton to="/sport/einheiten">Einheiten</LinkButton>
            {manages && <LinkButton to="/sport/hinweise">{(openRemarks.data?.count ?? 0) > 0 ? `Hinweise (${openRemarks.data?.count})` : 'Hinweise'}</LinkButton>}
            {canEdit && (
              <>
                <Button variant="secondary" onClick={() => setForm({ exercise: null, link: true })}>+ Linkübung</Button>
                <Button onClick={() => setForm({ exercise: null, link: false })}>+ Neue Übung</Button>
              </>
            )}
          </>
        }
      />

      <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        Die Trainingsplanung läuft als Test. Die Standardübungen werden noch von der Sektion Fußball durchgesehen –
        Rückmeldungen zu Übungen und zur Bedienung sind willkommen.
      </p>

      <ExerciseFilterBar filter={filter} onChange={setFilter} focusOptions={focusOptions} />

      <ExerciseListState query={list} />
      {list.data && list.data.length > 0 && (
        <>
          <p className="text-sm text-slate-500" aria-live="polite">{list.data.length} {list.data.length === 1 ? 'Übung' : 'Übungen'}</p>
          <ul className="grid gap-3 sm:grid-cols-2">
            {list.data.map((exercise) => (
              <ExerciseCard key={exercise.id} exercise={exercise} />
            ))}
          </ul>
        </>
      )}

      {id && !form && (
        <ExerciseDetailDialog
          key={id}
          id={id}
          canEdit={canEdit}
          onClose={() => navigate('/sport/uebungen')}
          onEdit={(exercise) => setForm({ exercise, link: exercise.isLink })}
          onCopied={(copy) => {
            navigate(`/sport/uebungen/${copy.id}`);
            setForm({ exercise: copy, link: copy.isLink });
          }}
        />
      )}

      {form && (
        <ExerciseFormDialog
          exercise={form.exercise}
          link={form.link}
          onClose={() => setForm(null)}
          onSaved={(saved) => navigate(`/sport/uebungen/${saved.id}`)}
        />
      )}
    </div>
  );
}
